import React, { useRef } from 'react';
import { Input, InputNumber, Segmented, Select, Space } from 'antd';

import ToggleRow from '../common/ToggleRow';

import BarcodeInspector from './BarcodeInspector';
import LayoutQueryFilter from './LayoutQueryFilter';
import PageGroupInspector from './PageGroupInspector';
import { elementLabel } from '../items/itemValueDisplay';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import { setBlockBox, setBlockPage, setBlockProps, setBlockStyle } from './pageLayoutModel';
import { blockPage } from './pageLayoutForm';
import { FLOOR_PT } from './pageBlockFit';

/** The blocks that draw text which auto scale can shrink (KPR-1862): a group block's cells, each alone (KPR-1863). */
const SCALES = new Set(['element', 'text', 'group']);

/**
 * The page designer's Selected block section: its box, its page of a form, what
 * it shows and its text style. Every control is a step, through the editor's
 * `change(fn, label, key)`, so undo sees it as it sees a drag.
 */
export default function PageBlockInspector({ selected, page, sheets, subject, elementDefs, queryFields, collectionId, change, onForm = false }) {
	// A group's columns scale with its box, and typing passes through every number on
	// the way (6, then empty, then 7, then 7.5): each scales the block as it stood when
	// the field took focus, so no keystroke's rounding is kept, and empty is no size.
	const typedFrom = useRef(null);
	const typeBox = (axis, value) => {
		if (selected.type === 'group' && value === null) return;
		const from = typedFrom.current?.id === selected.id ? typedFrom.current : null;
		change(latest => setBlockBox(latest, selected.id, { [axis]: value || 0 }, null, from), 'move', `box:${selected.id}:${axis}`);
	};
	return (
		<>
			<div className="micro-label page-editor__side-heading">Selected block</div>
			<Space wrap>
				{['x', 'y', 'w', ...(selected.type === 'rule' ? [] : ['h'])].map(axis => (
					<InputNumber
						key={axis}
						value={selected[axis]}
						onChange={value => typeBox(axis, value)}
						onFocus={() => { typedFrom.current = selected; }}
						onBlur={() => { typedFrom.current = null; }}
						aria-label={`Block ${axis}`}
						step={page.unit === 'mm' ? 1 : 0.125}
					/>
				))}
			</Space>
			{sheets.length > 1 ? (
				<Select
					value={blockPage(selected)}
					onChange={value => change(latest => setBlockPage(latest, selected.id, value), 'move')}
					aria-label="Block page"
					options={sheets.map(({ number }) => ({ value: number, label: `Page ${number}` }))}
				/>
			) : null}
			{selected.type === 'text' ? (
				<Input.TextArea
					value={selected.template || ''}
					onChange={event => change(latest => setBlockProps(latest, selected.id, { template: event.target.value }), 'text change', `template:${selected.id}`)}
					placeholder="Text for this block, which can include the item's own values"
					aria-label="Block text"
					rows={3}
				/>
			) : null}
			{selected.type === 'element' ? (
				<Select
					value={selected.element}
					onChange={value => change(latest => setBlockProps(latest, selected.id, { element: value }), 'field change')}
					aria-label="Block element"
					options={elementDefs.filter(def => def?.name).map(def => ({ value: def.name, label: elementLabel(def) }))}
				/>
			) : null}
			{selected.type === 'group' ? (
				<PageGroupInspector
					selected={selected}
					group={elementDefs.find(def => def?.name === selected.element) || null}
					unit={page.unit}
					change={change}
				/>
			) : null}
			{selected.type === 'barcode' ? (
				<BarcodeInspector
					block={selected}
					unit={page.unit}
					elementDefs={elementDefs}
					onProps={props => change(latest => setBlockProps(latest, selected.id, props), 'barcode change', `barcode:${selected.id}`)}
					onCode={code => change(latest => setBlockProps(latest, selected.id, { code }), 'barcode change', `barcode:${selected.id}`)}
				/>
			) : null}
			{selected.type === 'query' ? (
				<>
					<LayoutQueryFilter
						label="Block query"
						query={selected.query || ''}
						fields={queryFields}
						collectionId={collectionId}
						emptyText="No conditions yet. Add one to choose what the block lists."
						onChange={query => change(latest => setBlockProps(latest, selected.id, { query }), 'query change', `block-query:${selected.id}`)}
					/>
					<Segmented
						block
						value={selected.render || 'list'}
						onChange={value => change(latest => setBlockProps(latest, selected.id, { render: value }), 'display change')}
						options={[{ value: 'list', label: 'List' }, { value: 'table', label: 'Table' }, { value: 'grid', label: 'Grid' }]}
					/>
					{subject === 'record' ? (
						<Input
							{...NO_TEXT_ASSIST}
							value={selected.linkElement || ''}
							onChange={event => change(latest => setBlockProps(latest, selected.id, { linkElement: event.target.value }), 'link change', `link:${selected.id}`)}
							placeholder="Lookup on the other card pointing here"
							aria-label="Block link element"
						/>
					) : null}
				</>
			) : null}
			<Space wrap>
				<InputNumber
					value={selected.style?.size}
					onChange={value => change(latest => setBlockStyle(latest, selected.id, { size: value }), 'style change', `style-size:${selected.id}`)}
					aria-label="Block text size"
					placeholder="pt"
					min={4}
					max={96}
				/>
				<Select
					value={selected.style?.weight}
					onChange={value => change(latest => setBlockStyle(latest, selected.id, { weight: value }), 'style change')}
					aria-label="Block text weight"
					placeholder="Weight"
					allowClear
					options={[400, 500, 600, 700].map(w => ({ value: w, label: String(w) }))}
				/>
				<Select
					value={selected.style?.align}
					onChange={value => change(latest => setBlockStyle(latest, selected.id, { align: value }), 'style change')}
					aria-label="Block text align"
					placeholder="Align"
					allowClear
					options={['left', 'center', 'right'].map(a => ({ value: a, label: a }))}
				/>
			</Space>
			{/* Auto scale only means something where a box is a limit: on a form, for a block that draws text. */}
			{onForm && SCALES.has(selected.type) ? (
				<ToggleRow
					label="Auto scale to fit"
					description={`Shrinks a long value to fit this box, down to ${FLOOR_PT} pt, instead of cutting it.`}
					checked={selected.style?.autoScale === true}
					onChange={value => change(latest => setBlockStyle(latest, selected.id, { autoScale: value ? true : undefined }), 'style change')}
					first
				/>
			) : null}
		</>
	);
}
